import { Badge, Button, Group, Modal, NumberInput, Select, Stack, Switch, Table, Text, TextInput, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { type Schemas, api, call, hasPermission } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../components/common";
import { COUNT_MODES } from "./config/MachineTypesPage";

type Machine = Schemas["MachineOut"];

export function MachinesPage() {
  const { me } = useAuth();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Machine | "new" | null>(null);
  const machines = useQuery({
    queryKey: ["machines", showInactive],
    queryFn: () => call(api.GET("/api/v1/machines", { params: { query: { include_inactive: showInactive } } })),
  });
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const types = useQuery({ queryKey: ["machine-types"], queryFn: () => call(api.GET("/api/v1/machine-types")) });
  const factoryName = (id: string) => factories.data?.find((f) => f.id === id)?.name ?? "—";
  const typeName = (m: Machine) => types.data?.find((t) => t.id === m.machine_type_id)?.name ?? m.machine_type;
  const canManage = hasPermission(me, "machine:manage");

  if (machines.isLoading) return <Loading />;
  if (machines.error) return <ErrorAlert error={machines.error} />;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Machines</Title>
        <Group>
          <Switch label="Show inactive" checked={showInactive} onChange={(e) => setShowInactive(e.currentTarget.checked)} />
          {canManage && <Button onClick={() => setEditing("new")}>Register machine</Button>}
        </Group>
      </Group>
      <Table.ScrollContainer minWidth={720}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Code</Table.Th><Table.Th>Name</Table.Th><Table.Th>Type</Table.Th>
              <Table.Th>Factory</Table.Th><Table.Th>Make / model</Table.Th><Table.Th>Source</Table.Th><Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {machines.data!.map((m) => (
              <Table.Tr key={m.id} opacity={m.is_active ? 1 : 0.5}>
                <Table.Td><Link to={`/machines/${m.id}`}>{m.code}</Link></Table.Td>
                <Table.Td>{m.name} {!m.is_active && <Badge size="xs" color="gray">inactive</Badge>}</Table.Td>
                <Table.Td>{typeName(m)}</Table.Td>
                <Table.Td>{factoryName(m.factory_id)}</Table.Td>
                <Table.Td>{[m.manufacturer, m.model].filter(Boolean).join(" ") || "—"}</Table.Td>
                <Table.Td>
                  {m.is_simulated ? <Badge variant="outline" color="gray">simulated</Badge>
                    : m.gateway_id ? <Badge variant="light">gateway</Badge> : <Badge variant="light" color="teal">manual</Badge>}
                </Table.Td>
                <Table.Td>{canManage && <Button size="compact-xs" variant="default" onClick={() => setEditing(m)}>Edit</Button>}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {types.data?.length === 0 && (
        <Text size="sm" c="dimmed">Tip: define machine types first (Configuration → Machine types); a type can be imported from the catalogue.</Text>
      )}
      <Modal opened={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "Register machine" : "Edit machine"}>
        {editing && (
          <MachineForm factories={factories.data ?? []} types={types.data ?? []}
            machine={editing === "new" ? null : editing} onDone={() => setEditing(null)} />
        )}
      </Modal>
    </Stack>
  );
}

function MachineForm({ factories, types, machine, onDone }: {
  factories: Schemas["FactoryOut"][]; types: Schemas["MachineTypeOut"][]; machine: Machine | null; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    factory_id: machine?.factory_id ?? factories[0]?.id ?? "",
    gateway_id: machine?.gateway_id ?? "",
    line_id: machine?.line_id ?? "",
    machine_type_id: machine?.machine_type_id ?? types[0]?.id ?? "",
    name: machine?.name ?? "",
    code: machine?.code ?? "",
    manufacturer: machine?.manufacturer ?? "",
    model: machine?.model ?? "",
    is_simulated: machine?.is_simulated ?? false,
    is_active: machine?.is_active ?? true,
    ideal_cycle_time_s: (machine?.ideal_cycle_time_s ?? "") as number | "",
    count_mode: machine?.count_mode ?? "",
  });
  const gateways = useQuery({ queryKey: ["gateways"], queryFn: () => call(api.GET("/api/v1/gateways")) });
  const lines = useQuery({
    queryKey: ["lines", form.factory_id],
    enabled: !!form.factory_id,
    queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/lines", { params: { path: { factory_id: form.factory_id } } })),
  });
  const common = {
    name: form.name,
    line_id: form.line_id || null,
    gateway_id: form.gateway_id || null,
    machine_type_id: form.machine_type_id || null,
    manufacturer: form.manufacturer || null,
    model: form.model || null,
    ideal_cycle_time_s: form.ideal_cycle_time_s === "" ? null : form.ideal_cycle_time_s,
    count_mode: (form.count_mode || null) as Schemas["MachineUpdate"]["count_mode"],
  };
  const save = useMutation({
    mutationFn: () => machine
      ? call(api.PATCH("/api/v1/machines/{machine_id}", {
          params: { path: { machine_id: machine.id } }, body: { ...common, is_active: form.is_active },
        }))
      : call(api.POST("/api/v1/machines", {
          body: { ...common, factory_id: form.factory_id, code: form.code, is_simulated: form.is_simulated },
        })),
    onSuccess: () => {
      notifyOk(machine ? "Machine updated" : "Machine registered");
      void qc.invalidateQueries({ queryKey: ["machines"] });
      onDone();
    },
    onError: notifyError,
  });
  const set = (k: keyof typeof form) => (v: string | boolean | number | null) => setForm((f) => ({ ...f, [k]: v ?? "" }));
  const type = types.find((t) => t.id === form.machine_type_id);

  return (
    <Stack>
      <Select label="Factory" required disabled={!!machine} data={factories.map((f) => ({ value: f.id, label: f.name }))}
        value={form.factory_id} onChange={(v) => setForm((f) => ({ ...f, factory_id: v ?? "", line_id: "", gateway_id: "" }))} />
      <Group grow>
        <TextInput label="Code" required disabled={!!machine} placeholder="CNC-02" value={form.code}
          onChange={(e) => set("code")(e.currentTarget.value)} />
        <TextInput label="Name" required value={form.name} onChange={(e) => set("name")(e.currentTarget.value)} />
      </Group>
      <Select label="Machine type" required data={types.map((t) => ({ value: t.id, label: t.name }))}
        value={form.machine_type_id || null} onChange={set("machine_type_id")} />
      <Select label="Line (optional)" clearable data={(lines.data ?? []).filter((l) => l.is_active).map((l) => ({ value: l.id, label: l.name }))}
        value={form.line_id || null} onChange={set("line_id")} />
      <Group grow>
        <TextInput label="Manufacturer" value={form.manufacturer} onChange={(e) => set("manufacturer")(e.currentTarget.value)} />
        <TextInput label="Model" value={form.model} onChange={(e) => set("model")(e.currentTarget.value)} />
      </Group>
      <Group grow>
        <NumberInput label="Ideal cycle time (s)" min={0} decimalScale={3}
          placeholder={type?.ideal_cycle_time_s ? `type: ${type.ideal_cycle_time_s}` : "from product / type"}
          value={form.ideal_cycle_time_s} onChange={(v) => set("ideal_cycle_time_s")(v === "" ? "" : Number(v))} />
        <Select label="Production counted by" clearable data={COUNT_MODES}
          placeholder={`type: ${COUNT_MODES.find((m) => m.value === type?.count_mode)?.label ?? "—"}`}
          value={form.count_mode || null} onChange={set("count_mode")} />
      </Group>
      <Select label="Gateway (optional)" clearable placeholder="None — manual data entry"
        data={(gateways.data ?? []).filter((g) => g.factory_id === form.factory_id).map((g) => ({ value: g.id, label: g.name }))}
        value={form.gateway_id || null} onChange={set("gateway_id")} />
      {machine ? (
        <Switch label="Active" checked={form.is_active} onChange={(e) => set("is_active")(e.currentTarget.checked)} />
      ) : (
        <Switch label="Simulated machine" checked={form.is_simulated} onChange={(e) => set("is_simulated")(e.currentTarget.checked)} />
      )}
      <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!form.code || !form.name || !form.machine_type_id}>Save</Button>
    </Stack>
  );
}
